/**
 * Hexfloat reader. Correctly rounded, for f64 at least. :)
 *
 * A hexfloat is a float which is expressed in hexadecimal. The rules for Java in particular are as follows:
 *  - The float must be prefixed with 0x, have a p or P indicating the exponent, and some digits in the middle.
 *  - The dot is optional.
 *  - The exponent is in decimal and may or may not be prefixed with a + or -.
 *  - Besides the 0x prefix, ny sequence of digits within the float may strictly contain -- but not start or
 *    end with -- an underscore.
 */

type HexFloatParseResult =
	| {
			success: false;
			error: string;
			consumed: number;
	  }
	| {
			success: true;
			// Parsed value.
			value: number;
			// Whether the parsing was inexact.
			conversionWasInexact: boolean;
			// Number of bytes consumed when parsing.
			consumed: number;
			isF32: boolean;
	  };

const P2_50 = Math.pow(2, 50);
const P2_51 = P2_50 * 2;
const P2_52 = P2_51 * 2;

const f64Store = new Float64Array(1);
const intView = new Uint32Array(f64Store.buffer);

/**
 * Parse a hexadecimal float from a string.
 * @param s Float to parse.
 * @param offset Offset to read from in the string.
 */
export function parseHexFloat(s: string, offset = 0): HexFloatParseResult {
	// Accumulated mantissa bits, so that at the end, mantissaBits * 2^exponent is (ignoring issues of rounding)
	// our desired result.
	let mantissaBits = 0;
	// The sticky bit is 1 if any bits beyond the guard bits are nonzero
	let sticky = false;
	let exponent = 0;
	// Guard bits are those bits which follow the truncated mantissa, and are used to resolve ties.
	let guardBits = 0;
	// If guardBits < guardTie, we round toward zero. if guardBits == guardTie and sticky == false, we round to even.
	// Otherwise, we round up.
	let guardTie = 0;
	let digitsFound = false;
	let i = 0; // read pointer
	let isF32 = false;
	const maxRead = s.length - offset;
	let nonUnderscoreDigitsRead = 0;

	function readHexDigits(): boolean {
		const start = i;
		let endsInHex = false;
		nonUnderscoreDigitsRead = 0;
		for (; i < maxRead; ++i) {
			const char = s.charCodeAt(offset + i);
			if (char === 95 /* _ */) {
				endsInHex = true;
				if (i === start) return false;
				continue;
			}
			endsInHex = false;

			const decimal = (char - 48) /* 0 */ >>> 0;
			const lowercase = (char - 97) /* a */ >>> 0;
			const uppercase = (char - 65) /* A */ >>> 0;

			const hex = lowercase > uppercase ? uppercase : lowercase;
			const digit = decimal > 9 ? (hex > 5 ? -1 : hex + 10) : decimal;
			if (digit === -1) break;

			nonUnderscoreDigitsRead++;

			digitsFound = true;

			if (mantissaBits < 2251799813685248 /* 2 ** 49 */) {
				mantissaBits = mantissaBits * 16 + digit;
			} else {
				// The previous calculation may overflow. Calculate the number of bits that can be safely added and
				// store the rest as guard bits, or if guard bits are already calculated, the sticky bit.
				const chop =
					mantissaBits < P2_50
						? 0x1
						: mantissaBits < P2_51
							? 0x3
							: mantissaBits < P2_52
								? 0x7
								: 0xf;
				if (guardTie === 0) {
					// These are the guard bits to compute
					guardBits = digit & chop;
					mantissaBits = mantissaBits * 16 + (digit & ~chop);
					guardTie = (chop + 1) >> 1;
				} else {
					// Skip this digit as the guard bits are already computed, but keep track of the sticky bit
					sticky ||= (digit & chop) !== 0;
					exponent += 4;
				}
			}
		}
		return !endsInHex;
	}

	function err(message: string): HexFloatParseResult {
		return {
			success: false,
			error: message,
			consumed: i,
		};
	}

	function ok(value: number, inexact: boolean): HexFloatParseResult {
		if (isF32) {
			const rounded = Math.fround(value);
			inexact ||= rounded !== value;
			value = rounded;
		}

		return {
			success: true,
			value: sign * value,
			conversionWasInexact: inexact,
			consumed: i,
			isF32,
		};
	}

	function missingExponent() {
		return err("Missing hexfloat exponent");
	}

	let sign = 1;
	let signOrFirstDigit = s.charCodeAt(offset);
	if (signOrFirstDigit === 45 /* - */) {
		sign = -1;
		++i;
	} else i += +((signOrFirstDigit === 43) /* + */);

	const x = s.charCodeAt(offset + i + 1);
	if (
		s.charCodeAt(offset + i) !== 48 /* 0 */ ||
		(x !== 120 /* x */ && x !== 88) /* X */
	) {
		i += 2;
		return err("Invalid prefix");
	}

	i += 2;
	if (!readHexDigits())
		return err("Cannot start or end hexfloat digits with underscore");
	let next = s.charCodeAt(offset + i);
	if (i > maxRead - 1) return missingExponent();
	if (next === 46 /* . */) {
		++i;
		if (!readHexDigits())
			return err("Cannot start or end hexfloat digits with underscore");
		exponent -= 4 * nonUnderscoreDigitsRead;
		if (i > maxRead - 1) return missingExponent();
		next = s.charCodeAt(offset + i);
	}

	if (next !== 112 /* p */ && next !== 80 /* P */) return missingExponent();
	++i;
	if (!digitsFound) return err("No hexfloat digits");

	signOrFirstDigit = s.charCodeAt(offset + i);
	const expNegative = signOrFirstDigit === 45; /* - */
	if (expNegative || signOrFirstDigit === 43 /* + */) {
		if (++i === maxRead) return missingExponent();
	} else if (signOrFirstDigit === 95 /* _ */)
		return err("Cannot start hexfloat exponent digits with underscore");
	const expStart = i;
	let expVal = 0;
	let endsInUnderscore = false;
	for (; i < maxRead; ++i) {
		const c = s.charCodeAt(offset + i);
		endsInUnderscore = c === 95 /* _ */;
		if (endsInUnderscore) continue;

		const digit = (c - 48) >>> 0;
		if (digit > 9) {
			break;
		}
		expVal = 10 * expVal + digit;
	}
	if (endsInUnderscore)
		return err("Cannot end hexfloat exponent digits with underscore");

	if (i === expStart) return missingExponent();
	if (i !== maxRead) {
		switch (s.charCodeAt(offset + i)) {
			case 102 /* f */:
			case 70 /* F */:
				isF32 = true; // fallthrough
			case 100 /* d */:
			case 68 /* D */:
				++i;
		}
	}

	exponent += expNegative ? -expVal : expVal;

	if (mantissaBits === 0) {
		return ok(0, false);
	}

	// TODO: Add correctly rounded branch for f32
	f64Store[0] = mantissaBits;
	const floorLog2 = ((intView[1]! & 0x7ff00000) >> 20) - 1023;

	const finalExponent = exponent + floorLog2;
	if (finalExponent <= -1076) return ok(0, true);
	if (finalExponent <= -1023) {
		// Denormal case
		const truncatedBits = -1022 - finalExponent;

		let highMask = truncatedBits > 32 ? (1 << (truncatedBits - 32)) - 1 : 0;
		let lowMask = truncatedBits >= 32 ? -1 >>> 0 : (1 << truncatedBits) - 1;
		const highTie = (highMask + 1) >>> 1;
		const lowTie = (lowMask + 1) >>> 1;

		intView[1] = (intView[1]! & ~0x7ff00000) | 0x00100000; // add implicit 1 bit

		highMask &= intView[1];
		lowMask = (lowMask & intView[0]!) >>> 0;

		if (truncatedBits >= 32) {
			intView[0] = intView[1] >>> (truncatedBits - 32);
			intView[1] = 0;
		} else {
			intView[0] =
				(intView[1] << (32 - truncatedBits)) | (intView[0]! >>> truncatedBits);
			intView[1] = intView[1] >>> truncatedBits;
		}

		// Compare highMask/lowMask and highTie/lowTie, along with guard + sticky
		sticky ||= guardBits !== 0;

		if (highMask === 0 && lowMask === 0 && !sticky) {
			// exact case
			return ok(f64Store[0], false);
		} else if (
			highMask < highTie ||
			(highMask === highTie && lowMask < lowTie)
		) {
			// round down
			return ok(f64Store[0], true);
		} else if (highMask === highTie && lowMask === lowTie && !sticky) {
			// tie, inspect last bit and maybe round up
			if ((intView[0] & 1) === 0) {
				return ok(f64Store[0], true);
			}
		}
	} else {
		// normal case
		// Ensure the next calculation doesn't overflow
		if (exponent > 900) {
			mantissaBits *= 2 ** 256;
			exponent -= 256;
		} else if (exponent < -900) {
			mantissaBits *= 2 ** -256;
			exponent += 256;
		}

		const result = mantissaBits * 2 ** exponent;
		if (result === Infinity) return ok(Infinity, true);
		if (guardBits < guardTie || guardTie === 0)
			return ok(result, guardBits !== 0 || sticky);
		f64Store[0] = result;
		if (guardBits === guardTie && !sticky) {
			if ((intView[0]! & 1) === 0) {
				return ok(f64Store[0], true);
			}
		}
	}

	// Round up
	intView[1]! += +((intView[0]! += 1) >>> 0 === 0);
	return ok(f64Store[0]!, true);
}
